import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { EpisodeCard } from "@/components/podcast-episodes";
import { podcastEpisodesQuery } from "@/lib/podcast.functions";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { abs } from "@/content/seo";

const ogImage = abs(ogSocial);

const title = "Podcast Soft power | Sylwia Sitkowska";
const description =
  "Podcast „Soft power” Zwierciadła. Sylwia Sitkowska i Magdalena Kuszewska rozmawiają z zaproszonymi gośćmi. Nowe całe odcinki co dwa tygodnie.";

export const Route = createFileRoute("/podcast")({
  loader: ({ context }) => context.queryClient.ensureQueryData(podcastEpisodesQuery),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sylwiasitkowska.pl/podcast" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: "https://sylwiasitkowska.pl/podcast" }],
  }),
  component: PodcastPage,
});

function PodcastPage() {
  const { data: episodes } = useSuspenseQuery(podcastEpisodesQuery);
  const [latest, ...rest] = episodes;

  return (
    <>
      <SiteHeader />
      <main id="tresc-glowna">
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-steel pb-5">
              <p className="text-sm font-medium text-coral-text">Podcast</p>
            </div>
            <div className="mt-10 grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
              <div>
                <h1 className="display-xl text-5xl md:text-6xl">
                  Soft Power.
                  <span className="mt-4 block text-2xl leading-snug md:text-3xl">
                    Podcast o sile, która nie musi być głośna.
                  </span>
                </h1>
                <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink-soft">

                  Soft Power prowadzimy razem z dziennikarką Magdą Kuszewską, realizowany przez
                  magazynem Zwierciadło. Magda wnosi warsztat dziennikarski, ja perspektywę
                  psycholożki i przedsiębiorczyni, która na co dzień pracuje z ludźmi dźwigającymi
                  dużą odpowiedzialność. Zapraszam do odsłuchu.
                </p>
                <div className="mt-6 border-t border-steel pt-5 text-sm text-ink-soft">
                  <p>Nowe odcinki co dwa tygodnie</p>
                  <a
                    href="https://www.youtube.com/playlist?list=PLczQy25NMPvw"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 font-medium text-coral-text hover:underline"
                  >
                    Obejrzyj wszystkie odcinki na YouTube <ArrowUpRight aria-hidden="true" className="size-4" />
                  </a>
                </div>

              </div>
              {latest && <EpisodeCard episode={latest} featured />}
            </div>
          </div>
        </section>

        {rest.length > 0 && (
          <section aria-labelledby="odcinki" className="bg-steel-soft/40">
            <div className="container-page section-pad">
              <h2 id="odcinki" className="display-xl text-3xl md:text-4xl">Wcześniejsze odcinki</h2>
              <div className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((e) => (
                  <EpisodeCard key={e.videoId} episode={e} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </>
  );
}
